/**
 * Types for the MetricsExplorer time-series visualization
 */

// Time range preset options
export type TimePreset = '1h' | '6h' | '24h' | '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';

// Aggregation bucket sizes
export type AggregationLevel = 'raw' | '1m' | '5m' | '15m' | '1h' | '6h' | '1d';

// Display modes for the chart
export type ChartDisplayMode = 'unified' | 'split' | 'stacked' | 'normalized';

// Y-axis scale options
export type YAxisScale = 'auto' | 'manual' | 'log';

// Chart settings
export interface ChartSettings {
  displayMode: ChartDisplayMode;
  yAxisScale: YAxisScale;
  yAxisMin?: number;
  yAxisMax?: number;
  showPoints: boolean;
  lineWidth: number;
  fillArea: boolean;
  showGrid: boolean;
  showRangeslider: boolean;
}

// Metric statistics
export interface MetricStats {
  min: number;
  max: number;
  avg: number;
  stdDev: number;
  count: number;
}

// Single metric row from API
export interface MetricRow {
  id: string;
  createdAt: string;
  deviceId: number | null;
  metrics: Record<string, number>;
  _aggregation?: {
    bucket: string;
    pointCount: number;
  };
}

// API response for time-range metrics query
export interface MetricsTimeRangeResponse {
  status: number;
  data: MetricRow[];
  totalCount: number;
  timeRange: {
    from: string | null;
    to: string | null;
    actualFrom: string | null;
    actualTo: string | null;
  };
  aggregation: string | null;
  metricKeys: string[];
  stats: Record<string, MetricStats>;
}

// Query parameters for fetching metrics
export interface MetricsQueryParams {
  from?: string;
  to?: string;
  take?: number;
  aggregation?: AggregationLevel;
  metrics?: string[];
}

// Selected metric with color
export interface SelectedMetric {
  key: string;
  label: string;
  color: string;
  visible: boolean;
  yAxis?: 'y' | 'y2'; // For split Y-axis mode
}

// Metric group (for grouping by prefix)
export interface MetricGroup {
  prefix: string;
  metrics: string[];
  expanded: boolean;
}

// Annotation marker
export interface ChartAnnotation {
  id: string;
  timestamp: string;
  label: string;
  color: string;
  type: 'vertical' | 'horizontal';
  value?: number; // For horizontal lines
}

// Batch overlay band (a period assigned to a production batch)
export interface BatchOverlay {
  id: number;
  from: string;
  to: string;
  label: string;
  color: string;
}

// Threshold line
export interface ThresholdLine {
  id: string;
  metricKey: string;
  value: number;
  label: string;
  color: string;
  style: 'solid' | 'dashed' | 'dotted';
}

// Comparison period
export interface ComparisonPeriod {
  id: string;
  label: string;
  offsetMs: number; // e.g., -86400000 for "yesterday"
  color: string;
  enabled: boolean;
}

// Export format options
export type ExportFormat = 'csv' | 'json' | 'png' | 'svg';

// Export options
export interface ExportOptions {
  format: ExportFormat;
  includeStats: boolean;
  dateRange: 'visible' | 'all';
  selectedMetricsOnly: boolean;
}

// Live mode settings
export interface LiveModeSettings {
  enabled: boolean;
  intervalMs: number; // Refresh interval
  windowMs: number; // How much history to show
}

// Metric preset (saved configuration)
export interface MetricPreset {
  id: string;
  name: string;
  metrics: string[];
  chartSettings: ChartSettings;
  timePreset: TimePreset;
  aggregation: AggregationLevel;
}

// State for the MetricsExplorer component
export interface MetricsExplorerState {
  // Data
  data: MetricRow[];
  stats: Record<string, MetricStats>;
  metricKeys: string[];
  loading: boolean;
  error: string | null;

  // Time selection
  timePreset: TimePreset;
  customFrom: Date | null;
  customTo: Date | null;
  aggregation: AggregationLevel;

  // Metric selection
  selectedMetrics: SelectedMetric[];
  metricGroups: MetricGroup[];

  // Chart settings
  chartSettings: ChartSettings;

  // Annotations
  annotations: ChartAnnotation[];
  thresholds: ThresholdLine[];

  // Comparison
  comparisonPeriods: ComparisonPeriod[];

  // Live mode
  liveMode: LiveModeSettings;

  // Visible time range (after zoom)
  visibleFrom: Date | null;
  visibleTo: Date | null;
}

// Default colors for metrics (Plotly-compatible)
export const METRIC_COLORS = [
  '#00ff88', // Green
  '#ff6b6b', // Red
  '#7fb3ff', // Blue
  '#ffc107', // Yellow
  '#ba68c8', // Purple
  '#4dd0e1', // Cyan
  '#ff9800', // Orange
  '#ef5350', // Red-pink
  '#66bb6a', // Light green
  '#42a5f5', // Light blue
  '#ab47bc', // Magenta
  '#26c6da', // Teal
  '#ffca28', // Amber
  '#ec407a', // Pink
  '#7e57c2', // Deep purple
  '#26a69a', // Sea green
];

// Default chart settings
export const DEFAULT_CHART_SETTINGS: ChartSettings = {
  displayMode: 'unified',
  yAxisScale: 'auto',
  showPoints: false,
  lineWidth: 2,
  fillArea: false,
  showGrid: true,
  showRangeslider: true,
};

// Default live mode settings
export const DEFAULT_LIVE_MODE: LiveModeSettings = {
  enabled: false,
  intervalMs: 5000,
  windowMs: 3600000, // 1 hour
};

// Time preset configurations
export const TIME_PRESETS: Record<TimePreset, { label: string; ms: number | null }> = {
  '1h': { label: 'Last 1 hour', ms: 3600000 },
  '6h': { label: 'Last 6 hours', ms: 6 * 3600000 },
  '24h': { label: 'Last 24 hours', ms: 24 * 3600000 },
  '7d': { label: 'Last 7 days', ms: 7 * 24 * 3600000 },
  '30d': { label: 'Last 30 days', ms: 30 * 24 * 3600000 },
  '90d': { label: 'Last 90 days', ms: 90 * 24 * 3600000 },
  '1y': { label: 'Last year', ms: 365 * 24 * 3600000 },
  'all': { label: 'All time', ms: null },
  'custom': { label: 'Custom range', ms: null },
};

// Aggregation options
export const AGGREGATION_OPTIONS: { value: AggregationLevel; label: string }[] = [
  { value: 'raw', label: 'Auto (~5k points)' },
  { value: '1m', label: '1 minute' },
  { value: '5m', label: '5 minutes' },
  { value: '15m', label: '15 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '1d', label: '1 day' },
];
